import React, { useEffect, useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';

import './style.css';
import { GET_ROSETTA_DATA } from '../../urls';
import PricingTab from './Components/PricingTab';
import ImageModal from './Components/ImageModal';
import { fetchApiData } from '../../Utils/ApiCalls';
import RosettaHeader from './Components/RosettaHeader';
import { useRosettaStore } from '../../Stores/useRosettaStore';
import ScopeSelectionTab from './Components/ScopeSelectionTab';
import { showToast, StyleProvider } from '@attentive-platform/stem-ui';
import ResetConfirmationModal from './Components/ResetConfirmationModal';

const RosettaIndex = () => {
  const { loading, tradesData, showPricing, showResetModal, dispatch } = useRosettaStore(
    useShallow(state => ({
      loading: state.loading,
      tradesData: state.tradesData,
      showPricing: state.showPricing,
      showResetModal: state.showResetModal,
      dispatch: state.dispatch
    }))
  );

  const loadTradesData = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await fetchApiData({
        endpoint: GET_ROSETTA_DATA,
        apiRequest: 'GET'
      });

      if (response.error) {
        showToast(response.error, 'error');
        return;
      }

      const data = response.data;

      dispatch({ type: 'SET_TRADES_DATA', payload: data.trades || [] });
      dispatch({ type: 'SET_VOLUME_DISCOUNT_TIERS', payload: data.volume_discount || {} });

      // Initialize selected scopes with default selections
      const initialSelectedScopes = {};
      data.trades?.forEach(trade => {
        if (trade.scopes && Array.isArray(trade.scopes)) {
          initialSelectedScopes[trade.trade_id] = new Set(
            trade.scopes.filter(scope => scope.is_default_selected).map(scope => scope.scope_id)
          );
        }
      });
      dispatch({ type: 'SET_SELECTED_SCOPES', payload: initialSelectedScopes });
    } catch (error) {
      showToast(error, 'error');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch]);

  useEffect(() => {
    loadTradesData();
  }, [loadTradesData]);

  const resetConfiguration = () => {
    dispatch({ type: 'SET_SHOW_RESET_MODAL', payload: true });
  };

  const confirmReset = () => {
    dispatch({ type: 'RESET_CONFIGURATION' });
  };

  const cancelReset = () => {
    dispatch({ type: 'SET_SHOW_RESET_MODAL', payload: false });
  };

  if (loading && tradesData.length === 0) {
    return (
      <div className='rosetta-container'>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div>Loading Rosetta configuration...</div>
        </div>
      </div>
    );
  }

  return (
    <StyleProvider>
      <div className='rosetta-container'>
        {/* Header */}
        <RosettaHeader onReset={resetConfiguration} />

        {/* Main Content */}
        <main className='rosetta-main-content'>{showPricing ? <PricingTab /> : <ScopeSelectionTab />}</main>

        {/* Image Modal */}
        <ImageModal />

        {/* Reset Confirmation Modal */}
        <ResetConfirmationModal show={showResetModal} onClose={cancelReset} onConfirm={confirmReset} />
      </div>
    </StyleProvider>
  );
};

export default RosettaIndex;
